import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { AddCircleOutlineRounded } from '@mui/icons-material';
import DeleteForeverOutlined from '@mui/icons-material/DeleteForeverOutlined';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment';

import TimeFrame from 'features/residents/models/configurations/time-frame';
import { useTimeFrames } from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/time-frames/hooks';

import './time-frames.scss';

type Props = {
  timeFrames: TimeFrame[] | null;
  activeAllDay: boolean;
  valuesChanged: (newTimeFrames: TimeFrame[] | null, newActiveAllDay: boolean) => void;
  index: number;
  timeFramesDescription: string;
};

const TimeFrames = (props: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');
  const { viewingMode, validationErrors, timePickerLocaleText, addTimeFrame, removeTimeFrame, onTimeFrameChanged, setAllDayTimeFrame } =
    useTimeFrames(props);

  const renderTimeFrameRow = (timeFrame: TimeFrame, index: number) => (
    <Fragment key={`TimeFrame-${index}`}>
      <div className="time-pickers">
        <TimePicker
          label={t('scenarioConfiguration.from')}
          ampm={false}
          view={'minutes'}
          views={['hours', 'minutes']}
          format="HH:mm"
          localeText={timePickerLocaleText}
          value={moment(timeFrame.from, 'HH:mm:ss')}
          onChange={(value) => onTimeFrameChanged(value, index, true)}
          disabled={viewingMode === 'viewing'}
          className={`time-picker${validationErrors[index] ? ' validationError' : ''}`}
          slotProps={{
            popper: {
              className: 'time-picker-popper',
            },
          }}
        />
        <TimePicker
          label={t('scenarioConfiguration.to')}
          ampm={false}
          view={'minutes'}
          views={['hours', 'minutes']}
          format="HH:mm"
          localeText={timePickerLocaleText}
          value={moment(timeFrame.to, 'HH:mm:ss')}
          onChange={(value) => onTimeFrameChanged(value, index, false)}
          disabled={viewingMode === 'viewing'}
          className={`time-picker${validationErrors[index] ? ' validationError' : ''}`}
          slotProps={{
            popper: {
              className: 'time-picker-popper',
            },
          }}
        />
        {viewingMode !== 'viewing' && props.timeFrames && props.timeFrames.length > 1 && (
          <DeleteForeverOutlined
            onClick={() => removeTimeFrame(index)}
            className={`delete-icon ${validationErrors[index] && 'validationError'}`}
          />
        )}
      </div>
      {validationErrors[index] && (
        <div className="validationText">
          <Typography variant="caption" className="typographyColor">
            {t('scenarioConfiguration.timeFrameValidation')}
          </Typography>
        </div>
      )}
    </Fragment>
  );

  return (
    <div className="time-frames-container">
      <div className="time-frames-row">
        <Typography variant="subtitle1">{props.timeFramesDescription}</Typography>

        <div className="all-day-time-frame">
          <FormControl>
            <Checkbox checked={props.activeAllDay} onChange={setAllDayTimeFrame} disabled={viewingMode === 'viewing'} />
            <Typography variant="subtitle1">{t('scenarioConfiguration.scenarioAlwaysActive')}</Typography>
          </FormControl>
        </div>

        {props.timeFrames?.map(renderTimeFrameRow)}

        {viewingMode !== 'viewing' && !props.activeAllDay && (
          <Grid className="add-time-frame" onClick={addTimeFrame} onKeyDown={(e) => e.key === 'Enter' && addTimeFrame()}>
            <AddCircleOutlineRounded />
            <Typography variant="body1">{t('scenarioConfiguration.addTimeFrame')}</Typography>
          </Grid>
        )}
      </div>
    </div>
  );
};

export default TimeFrames;
